import {
  ChooseButton,
  ChooseButtonCheck,
  Price,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';

import Image from '@/app/components/Image';
import type { RingFinish } from '@/app/types/ring';

import type { Ring } from '../_hooks/types';
import useRingFinishChoice from './hooks/useRingFinishChoice';

export interface RingFinishChoiceProps {
  readonly active: boolean | undefined;
  readonly scrollRef: ((node: HTMLDivElement) => void) | undefined;
  readonly hasMaxRingCount: boolean | undefined;
  readonly isOuraRing4Wizard: boolean | undefined;
  readonly ring: Ring;
  readonly selectedFinish: RingFinish | undefined;
}

const RingFinishChoice = ({
  active,
  scrollRef,
  hasMaxRingCount,
  isOuraRing4Wizard,
  ring,
  selectedFinish,
}: RingFinishChoiceProps): JSX.Element => {
  const {
    colorMsgKey,
    currencyCode,
    discount,
    finish,
    isChosen,
    localizedAlt,
    originalPrice,
    src,
  } = useRingFinishChoice({ ring, selectedFinish });

  const t = useTranslations();
  return (
    <div ref={active && isChosen ? scrollRef : null} data-cy="finish-step">
      <ChooseButton
        className="flex items-stretch gap-1"
        data-cy={`choose-button-${finish}`}
        isDisabled={Boolean(hasMaxRingCount)}
        value={finish}
      >
        <div className="relative w-20 shrink-0 grow-0 overflow-hidden rounded-l">
          {isOuraRing4Wizard && src ? (
            <Image
              role="img"
              height={64}
              localizedAlt={localizedAlt}
              priority
              src={src}
              width={80}
              className="absolute size-full object-cover"
            />
          ) : (
            <div className="absolute -top-10 -left-20 w-48">
              {src && (
                <Image
                  role="img"
                  height={162.5}
                  localizedAlt={localizedAlt}
                  priority
                  src={src}
                  width={192}
                />
              )}
            </div>
          )}
        </div>

        <div className="flex flex-1 shrink items-center justify-between overflow-hidden py-5 pr-5 pl-1 md:pl-5">
          <p className="shrink text-nowrap sm:text-sm md:text-base">
            {t(colorMsgKey)}
          </p>
          <div className="flex grow items-center justify-end gap-1">
            <ChooseButtonCheck chosen={isChosen} />
            <Price
              className="text-sm md:text-base"
              currencyCode={currencyCode}
              discount={discount}
              price={originalPrice}
              priceCompare
              showZeroAsFree
            />
          </div>
        </div>
      </ChooseButton>
    </div>
  );
};

export default RingFinishChoice;
